import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ChatUser } from './chat.service';
import {
  ChatClientService,
  StreamI18nService,
  StreamAutocompleteTextareaModule,
  StreamChatModule,
  AutocompleteTextareaComponent,
  textareaInjectionToken,
  ChannelService,
  NotificationService,
} from 'stream-chat-angular';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { MOCK_SUPPORT_USER } from './mock-support-chat.config';
import { environment } from 'src/environments/environment';
import { ChatService } from './chat.service';
import { BehaviorSubject, firstValueFrom, of, Subject, takeUntil } from 'rxjs';
import { userProfileFeatureKey } from '../store/user/user.reducer';
import * as fromRoot from '../store/app.reducer';
import { UserState } from '../store/user/user.interface';
import { SharedModule } from '../shared/shared.module';
import { Router } from '@angular/router';
import { SupportMessageResponse } from './chat.service';
import {
  AnalyticsManagerService,
  Events,
} from '../shared/services/analytics-manager.service';

@Component({
  selector: 'app-support-chat',
  imports: [
    CommonModule,
    TranslateModule,
    StreamAutocompleteTextareaModule,
    StreamChatModule,
    SharedModule,
  ],
  templateUrl: './support-chat.component.html',
  styleUrls: ['./support-chat.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: 'translateY(0)', opacity: 1 })),
      state('out', style({ transform: 'translateY(100%)', opacity: 0 })),
      transition('out => in', [animate('300ms cubic-bezier(0.4,0,0.2,1)')]),
      transition('in => out', [animate('300ms cubic-bezier(0.4,0,0.2,1)')]),
    ]),
  ],
  providers: [
    {
      provide: textareaInjectionToken,
      useValue: AutocompleteTextareaComponent,
    },
  ]
})
export class SupportChatComponent implements OnInit, OnDestroy, AfterViewInit {
  customIcons: { [key: string]: string };
  isOpen = false;
  isVisible = false;
  user: ChatUser;
  messages = [];
  showTopics = true;
  isExpanded = false;
  isThreadOpen = false;
  userFirstName = '';
  showSupportButton = true;
  isMobile = window.innerWidth <= 675;
  location = window.location.pathname;

  private destroy$ = new Subject<void>();

  public isThreadOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  private messageNewHandler: any = null;

  private pendingDedupeKeys = new Set<string>();

  // Dedupe map: key = text|roundedTs, value = last seen timestamp (ms)
  private processedMessageDedupeMap = new Map<string, number>();
  private DEDUPE_WINDOW_MS = 5000; // 5 seconds
  private DEDUPE_ROUND_MS = 2000; // 2 seconds

  @ViewChild('chatBody') chatBody: ElementRef<HTMLDivElement>;

  constructor(
    private chatService: ChatService,
    private streamChatClientService: ChatClientService,
    private streamI18nService: StreamI18nService,
    private store: Store<fromRoot.AppState>,
    private cdr: ChangeDetectorRef,
    private channelService: ChannelService,
    private notificationService: NotificationService,
    private router: Router,
    private ngZone: NgZone
  ) { }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.chatBody && this.chatBody.nativeElement) {
        this.chatBody.nativeElement.scrollTo({
          top: this.chatBody.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 50);
  }

  async toggleChat() {
    // Analytics: Track Ask pipa button click
    if (!this.isOpen) {
      AnalyticsManagerService.recordButtonClick(Events.askPipaClicked);
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.isVisible = true;
      // Ensure both chatClient and channel are initialized
      if (!this.chatService.chatClient || !this.chatService.channel) {
        try {
          const clientChannel = await this.queryClientChannel();
          if (!clientChannel?.length) {
            await this.chatService.init(this.user);
            await this.queryClientChannel();
            await this.chatService.channel.watch();
          } else {
            this.chatService.channel = clientChannel[0];
          }
          this.scrollToBottom();
          this.setupChatBodyScroll();
          this.listenForUserMessages();
        } catch (error) {
          console.warn('Error initializing chat client:', error);
        }
      } else {
        this.setupChatBodyScroll();
        this.listenForUserMessages();
      }
      this.isThreadOpen = true;
    }
  }

  // Helper to check if user has sent a message this session
  private markUserSentMessage() {
    this.showTopics = false;
  }

  sendMessage(message) {
    this.markUserSentMessage();
    this.chatService.sendSupportMessage(message.text).subscribe({
      next: (response) => {
        this.scrollToBottom();
        return of(response);
      },
      error: (err) => {
        // console.error('Error sending support message to API:', err);
      },
    });
  }

  async sendSupportMessageFromTopic(question: string) {
    this.markUserSentMessage();
    // Send to Stream Chat so it appears in the chat UI
    if (this.chatService.channel) {
      await this.chatService.channel.sendMessage({ text: question });
    }
  }

  listenForUserMessages() {
    console.log('Listening for user messages...');
    if (!this.chatService.channel) return;
    // console.log('Channel:', this.chatService.channel);
    // Remove previous handler to avoid duplicates/loops
    if (this.messageNewHandler) {
      this.chatService.channel.off('message.new', this.messageNewHandler);
    }
    this.messageNewHandler = (event: any) => {
      const message = event.message;
      const sanitizedUserId = this.chatService.sanitizeStreamUserId(
        this.user.id
      );
      // Dedupe key: text + rounded created_at (2s window)
      const text = message.text?.trim() || '';
      const createdAt = message.created_at
        ? new Date(message.created_at).getTime()
        : Date.now();
      const roundedTs = Math.floor(createdAt / this.DEDUPE_ROUND_MS);
      const dedupeKey = `${text}|${roundedTs}`;
      const now = Date.now();
      // Clean up old dedupe keys
      for (const [key, ts] of this.processedMessageDedupeMap.entries()) {
        if (now - ts > this.DEDUPE_WINDOW_MS) {
          this.processedMessageDedupeMap.delete(key);
          this.pendingDedupeKeys.delete(key);
        }
      }

      if (
        message.user?.id === sanitizedUserId &&
        !this.processedMessageDedupeMap.has(dedupeKey) &&
        !this.pendingDedupeKeys.has(dedupeKey) &&
        !message.silent
      ) {
        this.markUserSentMessage();
        this.scrollToBottom();
        this.processedMessageDedupeMap.set(dedupeKey, now);
        this.pendingDedupeKeys.add(dedupeKey);
        this.chatService.sendSupportMessage(message.text).subscribe({
          next: (response: SupportMessageResponse) => {
            if (response.redirect) {
              //  run navigation inside Angular zone
              this.ngZone.run(() => {
                this.router.navigate([response.redirectUrl]);
              });
            }
            this.pendingDedupeKeys.delete(dedupeKey);
            this.scrollToBottom();
            return of(response);
          },
          error: (err) => {
            this.pendingDedupeKeys.delete(dedupeKey);
          },
        });
      } else {
        this.scrollToBottom();
      }
    };
    this.chatService.channel.on('message.new', this.messageNewHandler);
  }

  async queryClientChannel() {
    const sanitizedUserId = this.chatService.sanitizeStreamUserId(this.user.id);
    const ChatUser: ChatUser = {
      id: sanitizedUserId,
      name: this.user.name,
      image: this.user.image,
    };

    const token = await firstValueFrom(this.chatService.getToken(this.user));
    await this.streamChatClientService.init(
      environment.streamChatKey,
      ChatUser,
      token
    );

    const sanitizedStreamChatId = this.chatService.sanitizeStreamChatId(
      ChatUser.id
    );

    this.streamI18nService.setTranslation();
    return await this.channelService.init({
      type: 'messaging',
      id: { $eq: `support-${sanitizedStreamChatId}` },
    });
  }

  async closeChat() {
    this.isOpen = false;
    this.chatService.chatClient = null;
    await firstValueFrom(this.chatService.disconnect());
  }

  onAnimationDone(event: any) {
    if (event.toState === 'out') {
      this.isVisible = false;
    }
  }

  getUser() {
    this.store
      .select(userProfileFeatureKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userState: UserState) => {
        const fullName = userState.full_name;
        this.userFirstName = fullName.split(' ')[0] || fullName;
        this.cdr.detectChanges();
        this.user = environment.mockSupportChat
          ? MOCK_SUPPORT_USER
          : {
            id: userState.email,
            name: userState.full_name,
            image: userState?.ClientLinkedinMetaDatum?.profile_image,
          };
      });
  }

  private setupNavigationListener() {
    // Listen for navigation changes and update showSupportButton
    if (this.isMobile) return (this.showSupportButton = false);
    // this.router.events.subscribe(() => {
    //   const path = window.location.pathname;
    //   this.showSupportButton = !path.includes('leads');
    // });
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.scrollToBottom();
  }

  async ngOnInit() {
    this.getUser();
    this.setupNavigationListener();
  }

  ngAfterViewInit() {
    this.setupChatBodyScroll();
  }

  private setupChatBodyScroll() {
    setTimeout(() => {
      if (this.chatBody?.nativeElement) {
        const el = this.chatBody.nativeElement;
        el.addEventListener('wheel', (e: WheelEvent) => {
          e.preventDefault();
          el.scrollTop += e.deltaY;
        }, { passive: false });
      }
    }, 500);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chatService.chatClient) {
      this.chatService.chatClient.disconnectUser();
    }
  }
}
